import { useEffect, useRef, useState } from "react";

/** Delay before tearing down Spline — avoids flicker on quick scroll-past. */
const HIDE_DELAY_MS = 450;
const DEFER_MS = 700;

export function prefersReducedHeroMotion() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Mount Spline only while the hero is on screen, after a short idle defer.
 * Keeps first paint smooth and tears down WebGL when scrolled away.
 */
export function useHeroSplineMount(enabled = true, deferMs = DEFER_MS) {
  const wrapRef = useRef(null);
  const hideTimerRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [deferredReady, setDeferredReady] = useState(false);

  const allowSpline = Boolean(enabled) && !prefersReducedHeroMotion();

  useEffect(() => {
    if (!allowSpline) {
      setDeferredReady(false);
      return undefined;
    }

    let cancelled = false;
    const activate = () => {
      if (!cancelled) setDeferredReady(true);
    };

    if (typeof window.requestIdleCallback === "function") {
      const idleId = window.requestIdleCallback(activate, { timeout: deferMs });
      return () => {
        cancelled = true;
        window.cancelIdleCallback(idleId);
      };
    }

    const timerId = window.setTimeout(activate, deferMs);
    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [allowSpline, deferMs]);

  useEffect(() => {
    if (!allowSpline) {
      setInView(false);
      return undefined;
    }

    const node = wrapRef.current;
    if (!node) return undefined;

    const rect = node.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setInView(true);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
          }
          setInView(true);
          return;
        }

        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        hideTimerRef.current = setTimeout(() => {
          setInView(false);
          hideTimerRef.current = null;
        }, HIDE_DELAY_MS);
      },
      { rootMargin: "80px 0px", threshold: 0.02 },
    );

    observer.observe(node);
    return () => {
      observer.disconnect();
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
  }, [allowSpline]);

  const showSpline = allowSpline && deferredReady && inView;
  return { wrapRef, showSpline, allowSpline, inView };
}

/** @deprecated Use useHeroSplineMount */
export const useHeroSplineVisibility = useHeroSplineMount;
